// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import constants from "@store/constants";
import Factory from "@store/Library/Components/Icons/Factory";
import { StatusProps } from "@store/Library/Components/Status";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface ProductShipsFromPresentationProps extends ExpandStyles<ProductShipsFromStyles> {
    displayIcons?: boolean;
    displayTexts?: boolean;
}

export interface ProductShipsFromProps extends ProductShipsFromPresentationProps {
    pageType?: string;
    pricing?: ProductPriceDto | null;
    overrideWarehouseDescription?: string;
}

export interface ProductShipsFromStyles {
    loadingText?: TypographyPresentationProps;
    shipFromWrapper?: InjectableCss;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: TypographyPresentationProps;
    statusStyles?: StatusProps;
    ShipsFromMessage?: ShipsFromMessage;
    shipFromIcon?: TooltipPresentationProps;
}

interface ShipsFromMessage {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const productShipsFromStyles: ProductShipsFromStyles = {
    loadingText: {
        css: loadingDotsStyles(),
    },
    shipFromWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    shipFromIcon: {
        iconProps: {
            size: 20,
            src: Factory,
            color: "inherit",
        },
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
            `,
        },
    },
};

const ProductShipsFrom: FC<ProductShipsFromProps> = ({
    extendedStyles,
    overridedStyles,
    pageType,
    pricing,
    overrideWarehouseDescription,
    displayIcons = false,
    displayTexts = true,
}) => {
    const styles = useExpandProps(productShipsFromStyles, extendedStyles, overridedStyles);
    const { product, productInfo } = useContext(ProductContext);
    const isProductPage = useCurrentPage().type === constants.pageType.productDetails;
    const displayIcon = isProductPage ? true : displayIcons;

    if (pageType === "OrderConfirmationPage") {
        return null;
    }

    let warehouseDisplay: string | undefined;
    let isLoading = false;

    if (overrideWarehouseDescription) {
        warehouseDisplay = overrideWarehouseDescription;
    } else if (pricing) {
        warehouseDisplay = pricing.additionalResults?.warehouseDescription;
    } else if (product && productInfo) {
        // pricing (and therefore warehouseDescription) loads asynchronously after the product itself
        isLoading = !productInfo.pricing && !productInfo.failedToLoadPricing;
        warehouseDisplay = productInfo.pricing?.additionalResults?.warehouseDescription;
    }

    if (!isLoading && !warehouseDisplay) {
        // nothing to show: pricing failed to load, or this product has no assigned warehouse
        return null;
    }

    return (
        <StyledWrapper {...styles.shipFromWrapper} data-test-selector="product_ships_from">
            {displayIcon && (
                <Tooltip {...styles.shipFromIcon} text={siteMessage("ProductInfo_ShipFromIconMessage") as string} />
            )}
            <Typography {...styles.additionalInfoLabel}>
                {displayTexts && `${translate("{0}:", translate("Ships From"))}`}
            </Typography>
            {isLoading ? (
                <Typography {...styles.loadingText}>&nbsp;</Typography>
            ) : (
                <Typography {...styles.additionalInfoText}>{` ${warehouseDisplay}`}</Typography>
            )}
        </StyledWrapper>
    );
};

export default ProductShipsFrom;
